/**
 * The referee of the card region fork: given what each of the builder's two
 * paths produced for a card, and the text each paragraph is meant to read, it
 * says which path passed and which result is used.
 *
 * The two paths build the same card over the same paragraphs, after the card's
 * restores. The offset path edits only the characters the add-in's tracker
 * measured for the card's bookmark; the whole-paragraph path edits the whole
 * paragraphs, as the web and the core build. Rows 1 and 2 of the anchoring
 * tracker are the two ways the offset path has written the wrong words: a
 * bookmark covering no characters, and offsets measured before a restore
 * lengthened the paragraph.
 *
 * A path passes when its build did not throw and every paragraph of its result,
 * with every change accepted, reads as the intended text, compared after
 * `WordTokenizer.collapse` and by `WordTokenizer.comparisonKey`, the rule the
 * builder and the engine use to call two texts the same: spacing and Unicode
 * form aside. The engine writes nothing for a word that differs only in form,
 * so a paragraph that keeps the page's decomposed "e\u0301" still reads as intended.
 *
 * CASES, the pick
 *   - offset passes, whole fails  -> offset path
 *   - whole passes, offset fails  -> whole-paragraph path
 *   - both pass                   -> the one with fewer tracked changes; equal: the offset path
 *   - neither passes              -> none: the card is not written
 *
 * A disagreement is any build where the two packages differ or either path
 * failed; the add-in stores one record of it for review.
 */
import { WordTokenizer } from "./text/word-tokenizer";

export type RegionPathName = "offset_path" | "whole_paragraph_path";

/** What one path of the fork produced, as the referee judges it and the disagreement record keeps it. */
export interface RegionPathResult {
    passed: boolean;
    /** Each paragraph of the result with every change accepted; empty when the build threw. */
    texts: string[];
    changeCount: number;
    buildMs: number;
    /** The message of the error the build threw, a refused restore among them; null when it built. */
    error: string | null;
}

/** One path's build as the builder hands it over: the result's package too, which the record never stores. */
export interface RegionPathBuild {
    packageOoxml: string | null;
    texts: string[];
    changeCount: number;
    buildMs: number;
    error: string | null;
}

export interface RegionVerdict {
    /** The text each paragraph the card covers is meant to read: the three-way merge over the whole paragraph, after the restores. */
    intendedTexts: string[];
    offsetPath: RegionPathResult;
    wholeParagraphPath: RegionPathResult;
    used: RegionPathName | null;
    /** True when the two paths' packages differ or either path failed. */
    disagreement: boolean;
}

interface RegionJudgement {
    intendedTexts: string[];
    offsetPath: RegionPathBuild;
    wholeParagraphPath: RegionPathBuild;
}

export class ProposalRegionReferee {
    static judge({ intendedTexts, offsetPath, wholeParagraphPath }: RegionJudgement): RegionVerdict {
        const offsetResult = ProposalRegionReferee.judged(offsetPath, intendedTexts);
        const wholeResult = ProposalRegionReferee.judged(wholeParagraphPath, intendedTexts);
        return {
            intendedTexts,
            offsetPath: offsetResult,
            wholeParagraphPath: wholeResult,
            used: ProposalRegionReferee.pick(offsetResult, wholeResult),
            disagreement: offsetPath.error !== null || wholeParagraphPath.error !== null || offsetPath.packageOoxml !== wholeParagraphPath.packageOoxml,
        };
    }

    /**
     * A path passes when it built and every paragraph reads as intended.
     *
     * EXAMPLE
     *   intended ["…laws of the State of New York."], texts ["…laws of the State of New York.ork."] -> not passed
     *   intended ["…laws of the State of New York."], texts ["…laws of the State of New York."]     -> passed
     */
    private static judged(path: RegionPathBuild, intendedTexts: string[]): RegionPathResult {
        const readsAsIntended = path.error === null
            && path.texts.length === intendedTexts.length
            && path.texts.every((text, index) => WordTokenizer.readsTheSame(text, intendedTexts[index]));
        return { passed: readsAsIntended, texts: path.texts, changeCount: path.changeCount, buildMs: path.buildMs, error: path.error };
    }

    private static pick(offsetPath: RegionPathResult, wholeParagraphPath: RegionPathResult): RegionPathName | null {
        if (offsetPath.passed && !wholeParagraphPath.passed) return "offset_path";
        if (wholeParagraphPath.passed && !offsetPath.passed) return "whole_paragraph_path";
        if (!offsetPath.passed && !wholeParagraphPath.passed) return null;
        /**
         * WHEN both pass
         *   CONSEQUENCE
         *     the result with fewer tracked changes is the simpler redline of the same words;
         *     equal counts keep today's path.
         */
        return wholeParagraphPath.changeCount < offsetPath.changeCount ? "whole_paragraph_path" : "offset_path";
    }
}
